import { additionalSecurityChecks } from './additionalChecks';

export const assessmentsData = {
  aws: {
    name: 'AWS',
    categories: [
      {
        name: 'Identity & Access Management',
        icon: '🔐',
        checks: [
          { title: 'MFA enabled for root account', description: 'Multi-factor authentication should be enabled for the root account', risk: 'high' },
          { title: 'IAM password policy configured', description: 'Strong password policy enforced for all IAM users', risk: 'high' },
          { title: 'No hardcoded access keys', description: 'Access keys should not be embedded in code or configuration files', risk: 'high' },
          { title: 'Regular access key rotation', description: 'Access keys rotated every 90 days', risk: 'medium' },
          { title: 'Least privilege principle applied', description: 'IAM policies follow least privilege access', risk: 'high' },
          { title: 'Service accounts use IAM roles', description: 'EC2 instances and Lambda functions use IAM roles instead of keys', risk: 'medium' }
        ]
      },
      {
        name: 'Network Security',
        icon: '🌐',
        checks: [
          { title: 'Security groups restrict inbound traffic', description: 'No security groups allow 0.0.0.0/0 inbound access', risk: 'high' },
          { title: 'VPC flow logs enabled', description: 'Flow logs capture network traffic for analysis', risk: 'medium' },
          { title: 'Network ACLs configured', description: 'Additional layer of security at subnet level', risk: 'low' },
          { title: 'Private subnets for databases', description: 'RDS instances deployed in private subnets', risk: 'high' },
          { title: 'VPN or Direct Connect for admin access', description: 'Secure connection for administrative tasks', risk: 'medium' },
          { title: 'WAF enabled for web applications', description: 'Web Application Firewall protects against common attacks', risk: 'medium' }
        ]
      },
      {
        name: 'Data Protection',
        icon: '🔒',
        checks: [
          { title: 'S3 buckets encrypted at rest', description: 'All S3 buckets use server-side encryption', risk: 'high' },
          { title: 'S3 bucket policies restrict public access', description: 'No unintended public S3 buckets', risk: 'high' },
          { title: 'EBS volumes encrypted', description: 'All EBS volumes use encryption', risk: 'medium' },
          { title: 'RDS encryption enabled', description: 'Database instances encrypted at rest', risk: 'high' },
          { title: 'CloudTrail logs encrypted', description: 'Audit logs protected with encryption', risk: 'medium' },
          { title: 'KMS key rotation enabled', description: 'Automatic key rotation for encryption keys', risk: 'low' }
        ]
      },
      {
        name: 'Monitoring & Compliance',
        icon: '📊',
        checks: [
          { title: 'CloudTrail enabled in all regions', description: 'API activity logged across all regions', risk: 'high' },
          { title: 'CloudWatch alarms configured', description: 'Alerts for suspicious activities', risk: 'medium' },
          { title: 'Config rules enabled', description: 'Automated compliance checking', risk: 'medium' },
          { title: 'GuardDuty enabled', description: 'Threat detection service active', risk: 'medium' },
          { title: 'Security Hub enabled', description: 'Centralized security findings', risk: 'low' },
          { title: 'Regular security assessments', description: 'Periodic reviews and penetration testing', risk: 'medium' }
        ]
      }
    ]
  },
  azure: {
    name: 'Azure',
    categories: [
      {
        name: 'Identity & Access Management',
        icon: '🔐',
        checks: [
          { title: 'MFA enabled for all administrators', description: 'Multi-factor authentication for privileged accounts', risk: 'high' },
          { title: 'Azure AD Conditional Access policies', description: 'Risk-based access controls configured', risk: 'high' },
          { title: 'Service principals use managed identities', description: 'No hardcoded credentials in applications', risk: 'high' },
          { title: 'Privileged Identity Management (PIM) enabled', description: 'Just-in-time privileged access', risk: 'medium' },
          { title: 'Guest access regularly reviewed', description: 'External user access audited monthly', risk: 'medium' },
          { title: 'Legacy authentication disabled', description: 'Modern authentication protocols enforced', risk: 'high' }
        ]
      },
      {
        name: 'Network Security',
        icon: '🌐',
        checks: [
          { title: 'Network Security Groups properly configured', description: 'Restrictive inbound rules, no any-to-any', risk: 'high' },
          { title: 'Azure Firewall or NVA deployed', description: 'Centralized network security management', risk: 'medium' },
          { title: 'DDoS Protection Standard enabled', description: 'Protection against distributed attacks', risk: 'medium' },
          { title: 'Private endpoints for PaaS services', description: 'Services accessible only via private network', risk: 'high' },
          { title: 'Network Watcher enabled', description: 'Network monitoring and diagnostics active', risk: 'low' },
          { title: 'Application Gateway WAF enabled', description: 'Web application firewall for HTTP/S traffic', risk: 'medium' }
        ]
      },
      {
        name: 'Data Protection',
        icon: '🔒',
        checks: [
          { title: 'Storage accounts encrypted', description: 'Encryption at rest for all storage accounts', risk: 'high' },
          { title: 'Blob storage public access disabled', description: 'No anonymous access to storage containers', risk: 'high' },
          { title: 'SQL databases use TDE', description: 'Transparent Data Encryption enabled', risk: 'high' },
          { title: 'Key Vault for secrets management', description: 'Centralized secret and key management', risk: 'high' },
          { title: 'Disk encryption enabled', description: 'VM disks encrypted with Azure Disk Encryption', risk: 'medium' },
          { title: 'Soft delete enabled for Key Vault', description: 'Protection against accidental deletion', risk: 'medium' }
        ]
      },
      {
        name: 'Monitoring & Compliance',
        icon: '📊',
        checks: [
          { title: 'Azure Monitor configured', description: 'Comprehensive monitoring solution active', risk: 'medium' },
          { title: 'Activity logs retained', description: 'Audit logs stored for compliance period', risk: 'high' },
          { title: 'Microsoft Defender for Cloud enabled', description: 'Cloud security posture management active', risk: 'high' },
          { title: 'Policy compliance monitoring', description: 'Azure Policy tracking compliance', risk: 'medium' },
          { title: 'Update management configured', description: 'Automated patching for VMs', risk: 'high' },
          { title: 'Cost anomaly detection enabled', description: 'Alerts for unusual spending patterns', risk: 'low' }
        ]
      }
    ]
  },
  gcp: {
    name: 'Google Cloud',
    categories: [
      {
        name: 'Identity & Access Management',
        icon: '🔐',
        checks: [
          { title: '2-Step Verification enforced', description: 'MFA required for all users', risk: 'high' },
          { title: 'Service accounts follow least privilege', description: 'Minimal permissions granted to service accounts', risk: 'high' },
          { title: 'Organization policies configured', description: 'Centralized security controls applied', risk: 'medium' },
          { title: 'Workload Identity enabled', description: 'Secure workload authentication to Google APIs', risk: 'medium' },
          { title: 'Binary Authorization enabled', description: 'Only trusted container images deployed', risk: 'medium' },
          { title: 'Context-aware access configured', description: 'Access based on user and device context', risk: 'low' }
        ]
      },
      {
        name: 'Network Security',
        icon: '🌐',
        checks: [
          { title: 'VPC firewall rules restrictive', description: 'Default deny with specific allow rules', risk: 'high' },
          { title: 'Private Google Access enabled', description: 'Access Google services without public IPs', risk: 'medium' },
          { title: 'Cloud Armor configured', description: 'DDoS and application attack protection', risk: 'medium' },
          { title: 'VPC Flow Logs enabled', description: 'Network traffic logging for analysis', risk: 'medium' },
          { title: 'Cloud NAT for egress traffic', description: 'Controlled outbound internet access', risk: 'low' },
          { title: 'Private Service Connect used', description: 'Private connectivity to services', risk: 'medium' }
        ]
      },
      {
        name: 'Data Protection',
        icon: '🔒',
        checks: [
          { title: 'Cloud Storage encryption', description: 'Default encryption for all buckets', risk: 'high' },
          { title: 'Bucket IAM policies restrictive', description: 'No public access unless required', risk: 'high' },
          { title: 'Customer-managed encryption keys', description: 'CMEK used for sensitive data', risk: 'medium' },
          { title: 'Data Loss Prevention configured', description: 'DLP policies scan for sensitive data', risk: 'medium' },
          { title: 'BigQuery dataset encryption', description: 'Analytics data encrypted at rest', risk: 'medium' },
          { title: 'Secret Manager for credentials', description: 'No hardcoded secrets in code', risk: 'high' }
        ]
      },
      {
        name: 'Monitoring & Compliance',
        icon: '📊',
        checks: [
          { title: 'Cloud Audit Logs enabled', description: 'Admin and data access logged', risk: 'high' },
          { title: 'Security Command Center activated', description: 'Centralized security insights', risk: 'medium' },
          { title: 'Cloud Asset Inventory tracking', description: 'Complete visibility of resources', risk: 'medium' },
          { title: 'Forseti or Policy Intelligence', description: 'Automated compliance scanning', risk: 'medium' },
          { title: 'Budget alerts configured', description: 'Cost control mechanisms in place', risk: 'low' },
          { title: 'Container Analysis enabled', description: 'Vulnerability scanning for containers', risk: 'medium' }
        ]
      }
    ]
  },
  alibaba: {
    name: 'Alibaba Cloud',
    categories: [
      {
        name: 'Identity & Access Management',
        icon: '🔐',
        checks: [
          { title: 'RAM MFA enabled for all users', description: 'Multi-factor authentication for Resource Access Management', risk: 'high' },
          { title: 'RAM policies follow least privilege', description: 'Minimal permissions granted to users and roles', risk: 'high' },
          { title: 'AccessKey rotation implemented', description: 'Regular rotation of access credentials', risk: 'medium' },
          { title: 'STS tokens used for temporary access', description: 'Security Token Service for short-lived credentials', risk: 'medium' },
          { title: 'Resource groups properly configured', description: 'Logical grouping for access control', risk: 'medium' },
          { title: 'ActionTrail enabled for all regions', description: 'Audit trail for API calls and actions', risk: 'high' }
        ]
      },
      {
        name: 'Network Security',
        icon: '🌐',
        checks: [
          { title: 'Security groups properly configured', description: 'Restrictive inbound rules, deny by default', risk: 'high' },
          { title: 'VPC isolation implemented', description: 'Network segmentation between workloads', risk: 'high' },
          { title: 'Anti-DDoS Pro/Premium enabled', description: 'Protection against distributed attacks', risk: 'medium' },
          { title: 'Cloud Firewall configured', description: 'Centralized firewall management', risk: 'medium' },
          { title: 'PrivateZone for internal DNS', description: 'Private DNS resolution within VPC', risk: 'low' },
          { title: 'VPN Gateway or Express Connect', description: 'Secure hybrid cloud connectivity', risk: 'medium' }
        ]
      },
      {
        name: 'Data Protection',
        icon: '🔒',
        checks: [
          { title: 'OSS bucket encryption enabled', description: 'Object Storage Service encryption at rest', risk: 'high' },
          { title: 'OSS bucket policies restrictive', description: 'No public access unless required', risk: 'high' },
          { title: 'RDS TDE enabled', description: 'Transparent Data Encryption for databases', risk: 'high' },
          { title: 'KMS integration configured', description: 'Key Management Service for encryption keys', risk: 'medium' },
          { title: 'Data Encryption Service used', description: 'Hardware-based encryption for sensitive data', risk: 'medium' },
          { title: 'SSL certificates properly managed', description: 'Certificate management and renewal', risk: 'medium' }
        ]
      },
      {
        name: 'Monitoring & Compliance',
        icon: '📊',
        checks: [
          { title: 'Cloud Monitor configured', description: 'Comprehensive monitoring for all resources', risk: 'medium' },
          { title: 'Log Service enabled', description: 'Centralized log collection and analysis', risk: 'high' },
          { title: 'Security Center activated', description: 'Unified security management platform', risk: 'high' },
          { title: 'Config compliance rules set', description: 'Automated compliance checking', risk: 'medium' },
          { title: 'Threat Detection Service enabled', description: 'Advanced threat detection capabilities', risk: 'medium' },
          { title: 'Regular vulnerability scanning', description: 'Automated security assessments', risk: 'medium' }
        ]
      }
    ]
  },
  ibm: {
    name: 'IBM Cloud',
    categories: [
      {
        name: 'Identity & Access Management',
        icon: '🔐',
        checks: [
          { title: 'IBM Cloud IAM MFA enforced', description: 'Multi-factor authentication for all users', risk: 'high' },
          { title: 'Service IDs with API keys managed', description: 'Proper service authentication setup', risk: 'high' },
          { title: 'Access groups configured', description: 'Role-based access control implementation', risk: 'medium' },
          { title: 'Trusted profiles utilized', description: 'Compute resource authentication without keys', risk: 'medium' },
          { title: 'Activity Tracker enabled', description: 'Audit logging for compliance', risk: 'high' },
          { title: 'Federation with enterprise IdP', description: 'SSO integration with corporate identity', risk: 'medium' }
        ]
      },
      {
        name: 'Network Security',
        icon: '🌐',
        checks: [
          { title: 'VPC security groups configured', description: 'Network ACLs and security groups set', risk: 'high' },
          { title: 'Flow Logs for VPC enabled', description: 'Network traffic monitoring active', risk: 'medium' },
          { title: 'Cloud Internet Services (CIS) enabled', description: 'DDoS protection and WAF configured', risk: 'medium' },
          { title: 'Private endpoints used', description: 'Service endpoints within VPC', risk: 'high' },
          { title: 'Transit Gateway configured', description: 'Secure multi-VPC connectivity', risk: 'medium' },
          { title: 'Network segmentation implemented', description: 'Proper subnet isolation', risk: 'high' }
        ]
      },
      {
        name: 'Data Protection',
        icon: '🔒',
        checks: [
          { title: 'Cloud Object Storage encryption', description: 'Encryption enabled for all buckets', risk: 'high' },
          { title: 'Key Protect or HPCS integration', description: 'Hardware security module usage', risk: 'high' },
          { title: 'Database encryption enabled', description: 'Encryption at rest for databases', risk: 'high' },
          { title: 'Secrets Manager utilized', description: 'Centralized secret management', risk: 'high' },
          { title: 'Certificate Manager configured', description: 'SSL/TLS certificate lifecycle management', risk: 'medium' },
          { title: 'Backup and disaster recovery', description: 'Regular backups with encryption', risk: 'medium' }
        ]
      },
      {
        name: 'Monitoring & Compliance',
        icon: '📊',
        checks: [
          { title: 'IBM Cloud Monitoring active', description: 'Platform and application monitoring', risk: 'medium' },
          { title: 'Log Analysis configured', description: 'Centralized log management', risk: 'high' },
          { title: 'Security and Compliance Center', description: 'Continuous compliance monitoring', risk: 'high' },
          { title: 'Vulnerability Advisor enabled', description: 'Container image scanning', risk: 'medium' },
          { title: 'Cloud Pak for Security integrated', description: 'Advanced threat management', risk: 'low' },
          { title: 'Cost and asset management', description: 'Resource tracking and optimization', risk: 'low' }
        ]
      }
    ]
  },
  oracle: {
    name: 'Oracle Cloud',
    categories: [
      {
        name: 'Identity & Access Management',
        icon: '🔐',
        checks: [
          { title: 'IDCS MFA enabled', description: 'Identity Cloud Service multi-factor authentication', risk: 'high' },
          { title: 'Compartment structure defined', description: 'Logical isolation of resources', risk: 'high' },
          { title: 'IAM policies follow least privilege', description: 'Granular permissions implementation', risk: 'high' },
          { title: 'Instance principals configured', description: 'Compute instances authenticate without keys', risk: 'medium' },
          { title: 'Adaptive authentication enabled', description: 'Risk-based authentication rules', risk: 'medium' },
          { title: 'API signing keys rotated', description: 'Regular rotation of API credentials', risk: 'medium' }
        ]
      },
      {
        name: 'Network Security',
        icon: '🌐',
        checks: [
          { title: 'Security lists properly configured', description: 'Stateful firewall rules implemented', risk: 'high' },
          { title: 'Network Security Groups used', description: 'Application-tier security controls', risk: 'medium' },
          { title: 'WAF enabled for applications', description: 'Web Application Firewall protection', risk: 'medium' },
          { title: 'Bastion service for access', description: 'Secure administrative access', risk: 'medium' },
          { title: 'Service Gateway configured', description: 'Private access to Oracle services', risk: 'medium' },
          { title: 'VCN Flow Logs enabled', description: 'Virtual Cloud Network traffic logging', risk: 'medium' }
        ]
      },
      {
        name: 'Data Protection',
        icon: '🔒',
        checks: [
          { title: 'Object Storage encryption', description: 'Bucket encryption with customer keys', risk: 'high' },
          { title: 'Block Volume encryption', description: 'Boot and block volumes encrypted', risk: 'high' },
          { title: 'Database TDE enabled', description: 'Transparent Data Encryption for databases', risk: 'high' },
          { title: 'Vault service utilized', description: 'Centralized key management', risk: 'high' },
          { title: 'Data Safe configured', description: 'Database security assessment and masking', risk: 'medium' },
          { title: 'Database vault enabled', description: 'Privileged user controls for databases', risk: 'medium' }
        ]
      },
      {
        name: 'Monitoring & Compliance',
        icon: '📊',
        checks: [
          { title: 'Cloud Guard enabled', description: 'Cloud security posture management', risk: 'high' },
          { title: 'Audit service configured', description: 'API activity logging enabled', risk: 'high' },
          { title: 'Events service rules set', description: 'Automated responses to events', risk: 'medium' },
          { title: 'Logging Analytics active', description: 'Centralized log analysis', risk: 'medium' },
          { title: 'Compliance documents maintained', description: 'Oracle compliance certifications tracked', risk: 'low' },
          { title: 'Security zones implemented', description: 'Enforce security best practices', risk: 'medium' }
        ]
      }
    ]
  },
  multi: {
    name: 'Multi-Cloud',
    categories: [
      {
        name: 'Governance & Strategy',
        icon: '🎯',
        checks: [
          { title: 'Centralized identity provider', description: 'Single sign-on across all clouds', risk: 'high' },
          { title: 'Consistent security policies', description: 'Standardized controls across providers', risk: 'high' },
          { title: 'Multi-cloud SIEM solution', description: 'Unified security monitoring', risk: 'high' },
          { title: 'Cloud-agnostic backup strategy', description: 'Cross-cloud disaster recovery', risk: 'medium' },
          { title: 'Vendor lock-in mitigation', description: 'Portable architectures and data formats', risk: 'medium' },
          { title: 'Cost optimization across clouds', description: 'Multi-cloud cost management tools', risk: 'low' }
        ]
      },
      {
        name: 'Container & Kubernetes Security',
        icon: '🐳',
        checks: [
          { title: 'Container image scanning', description: 'Vulnerability scanning in CI/CD pipeline', risk: 'high' },
          { title: 'RBAC properly configured', description: 'Kubernetes role-based access control', risk: 'high' },
          { title: 'Network policies enforced', description: 'Micro-segmentation between pods', risk: 'medium' },
          { title: 'Secrets management solution', description: 'External secret storage (Vault, etc.)', risk: 'high' },
          { title: 'Pod security policies/standards', description: 'Runtime security controls', risk: 'medium' },
          { title: 'Service mesh security', description: 'mTLS and policy enforcement', risk: 'medium' }
        ]
      },
      {
        name: 'DevSecOps Integration',
        icon: '🔧',
        checks: [
          { title: 'Infrastructure as Code scanning', description: 'Security checks for Terraform/CloudFormation', risk: 'high' },
          { title: 'Automated compliance checks', description: 'Policy as Code implementation', risk: 'medium' },
          { title: 'Security gates in CI/CD', description: 'Automated security testing', risk: 'high' },
          { title: 'Dependency vulnerability scanning', description: 'Third-party library security', risk: 'high' },
          { title: 'Code security analysis', description: 'SAST/DAST tools integrated', risk: 'medium' },
          { title: 'Shift-left security practices', description: 'Security integrated early in SDLC', risk: 'medium' }
        ]
      },
      {
        name: 'Hybrid Cloud Considerations',
        icon: '🔄',
        checks: [
          { title: 'Secure connectivity established', description: 'VPN/dedicated circuits to on-premises', risk: 'high' },
          { title: 'Consistent access controls', description: 'Identity federation configured', risk: 'high' },
          { title: 'Data classification implemented', description: 'Clear policies on data placement', risk: 'medium' },
          { title: 'Egress cost optimization', description: 'Minimize cross-cloud data transfer', risk: 'low' },
          { title: 'Compliance boundaries defined', description: 'Data residency requirements met', risk: 'high' },
          { title: 'Unified monitoring dashboard', description: 'Single pane of glass visibility', risk: 'medium' }
        ]
      }
    ]
  }
};

// Add common checks to all providers
Object.keys(assessmentsData).forEach(provider => {
  additionalSecurityChecks.common.forEach(category => {
    assessmentsData[provider].categories.push({
      name: category.category,
      icon: category.category.includes('API') ? '🔌' : 
            category.category.includes('Serverless') ? '⚡' : 
            category.category.includes('Container') ? '🐳' : 
            category.category.includes('Cost') ? '💰' :
            category.category.includes('Data Sovereignty') ? '🌍' :
            category.category.includes('Threat') ? '🛡️' : '⚙️',
      checks: category.checks
    });
  });
});

// Add provider-specific checks
Object.keys(additionalSecurityChecks).forEach(provider => {
  if (provider !== 'common' && assessmentsData[provider]) {
    additionalSecurityChecks[provider].forEach(category => {
      assessmentsData[provider].categories.push({
        name: category.category,
        icon: category.category.includes('Quantum') ? '⚛️' : 
              category.category.includes('AWS') ? '☁️' :
              category.category.includes('Azure') ? '🔷' :
              category.category.includes('GCP') ? '🌐' :
              category.category.includes('Multi-Cloud') ? '🔀' : '⚙️',
        checks: category.checks
      });
    });
  }
}); 